/**
 * App's Media Reports 主頁面
 * 整合所有組件，提供完整的媒體報告功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Space, message, Spin, Progress, Divider, Typography, Tooltip, Radio } from 'antd';
import { 
  ReloadOutlined, 
  FilePdfOutlined,
  FileWordOutlined,
  FileZipOutlined,
  FileImageOutlined,
  FilterOutlined,
  UpOutlined,
  DownOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useLanguage } from '../../../contexts/LanguageContext';
import InstanceFilters from './components/InstanceFilters';
import ProcessVariableFilters from './components/ProcessVariableFilters';
import GallerySettings from './components/GallerySettings';
import MediaGallery from './components/MediaGallery';
import { groupMediaByExecutionAndPV } from './utils/mediaGrouping';
import { exportGalleryToPdf } from './utils/pdfExport';
import { exportGalleryToDocx } from './utils/docxExport';
import { exportGalleryToZip } from './utils/zipExport';
import {
  loadAppsMediaSelection,
  saveAppsMediaSelection,
  loadAppsMediaQueryState,
  saveAppsMediaQueryState,
  loadAppsMediaGallerySettings,
  saveAppsMediaGallerySettings,
  loadAppsMediaUserExcluded,
  saveAppsMediaUserExcluded,
} from '../../../services/uiPreferenceApi';

const { Text } = Typography;

const QUERY_SAVE_DEBOUNCE_MS = 1000;
const SETTINGS_SAVE_DEBOUNCE_MS = 1000;
const SELECTION_SAVE_DEBOUNCE_MS = 800;

const DEFAULT_GALLERY_SETTINGS = {
  maxImagesPerPage: 4,
  imageGap: 5,
  minImageWidth: 50,
  pdfOrientation: 'landscape',
  imageQuality: 0.9,
};

const getDefaultQueryState = () => ({
  filters: { status: 'all', workflowName: '', startDateRange: null },
  pvFilters: [],
  groupingPvs: [],
  groupingEformFields: [],
  showPvFilters: false,
});

const AppsMediaReportsPage = () => {
  const { t } = useLanguage();
  
  // 狀態管理 — 初始值用 default，待 useEffect 從 DB 載入後覆蓋
  const [filters, setFilters] = useState(() => getDefaultQueryState().filters);
  const [pvFilters, setPvFilters] = useState([]);
  const [groupingPvs, setGroupingPvs] = useState([]);
  const [groupingEformFields, setGroupingEformFields] = useState([]);
  const [showPvFilters, setShowPvFilters] = useState(false);
  const [queryStateLoaded, setQueryStateLoaded] = useState(false);

  const [processVariableDefinitions, setProcessVariableDefinitions] = useState([]);
  const [groupedMedia, setGroupedMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  
  const [gallerySettings, setGallerySettings] = useState(DEFAULT_GALLERY_SETTINGS);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedByGroup, setSelectedByGroup] = useState({});
  const [excludedByGroup, setExcludedByGroup] = useState({});
  const [selectionLoading, setSelectionLoading] = useState(false);
  const selectionSaveTimerRef = useRef(null);
  const excludedSaveTimerRef = useRef(null);
  const querySaveTimerRef = useRef(null);
  const settingsSaveTimerRef = useRef(null);

  // 從 DB 載入查詢條件 + 畫廊設定（僅 mount 時一次）
  useEffect(() => {
    Promise.all([loadAppsMediaQueryState(), loadAppsMediaGallerySettings()]).then(
      ([qs, gs]) => {
        setFilters(qs.filters);
        setPvFilters(qs.pvFilters);
        setGroupingPvs(qs.groupingPvs);
        setGroupingEformFields(qs.groupingEformFields);
        setShowPvFilters(qs.showPvFilters);
        setGallerySettings({ ...DEFAULT_GALLERY_SETTINGS, ...gs });
        setQueryStateLoaded(true);
      }
    );
    return () => {
      if (querySaveTimerRef.current) clearTimeout(querySaveTimerRef.current);
      if (settingsSaveTimerRef.current) clearTimeout(settingsSaveTimerRef.current);
      if (excludedSaveTimerRef.current) clearTimeout(excludedSaveTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 當 workflowName 改變時，從 DB 載入已儲存的勾選設定 + 不列印標記
  useEffect(() => {
    const workflowName = filters.workflowName;
    if (!workflowName) {
      setSelectedByGroup({});
      setExcludedByGroup({});
      return;
    }
    let cancelled = false;
    setSelectionLoading(true);
    Promise.all([
      loadAppsMediaSelection(workflowName),
      loadAppsMediaUserExcluded(workflowName),
    ]).then(([saved, excluded]) => {
      if (!cancelled) {
        setSelectedByGroup(saved);
        setExcludedByGroup(excluded);
      }
    }).catch(() => {
      if (!cancelled) { setSelectedByGroup({}); setExcludedByGroup({}); }
    }).finally(() => { if (!cancelled) setSelectionLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.workflowName]);

  // 更新某 subGroup 的選取清單，並 debounced 存回 DB
  const handleSelectionChange = useCallback((subGroupKey, orderedPaths) => {
    setSelectedByGroup(prev => {
      const next = { ...prev, [subGroupKey]: orderedPaths };
      if (selectionSaveTimerRef.current) clearTimeout(selectionSaveTimerRef.current);
      selectionSaveTimerRef.current = setTimeout(() => {
        const workflowName = filters.workflowName;
        if (workflowName) {
          saveAppsMediaSelection(workflowName, next).catch(err =>
            console.error('[AppsMediaReports] 儲存勾選設定失敗:', err)
          );
        }
      }, SELECTION_SAVE_DEBOUNCE_MS);
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.workflowName]);

  // 更新某 subGroup 的「不列印」標記，並 debounced 存回 DB
  const handleExcludedChange = useCallback((subGroupKey, newExcludedPaths) => {
    setExcludedByGroup(prev => {
      const next = { ...prev, [subGroupKey]: newExcludedPaths };
      if (excludedSaveTimerRef.current) clearTimeout(excludedSaveTimerRef.current);
      excludedSaveTimerRef.current = setTimeout(() => {
        const workflowName = filters.workflowName;
        if (workflowName) {
          saveAppsMediaUserExcluded(workflowName, next).catch(err =>
            console.error('[AppsMediaReports] 儲存不列印標記失敗:', err)
          );
        }
      }, SELECTION_SAVE_DEBOUNCE_MS);
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.workflowName]);

  // 查詢條件改變時 debounced 存回 DB（queryStateLoaded 後才開始，避免 mount 初始值覆蓋已存設定）
  useEffect(() => {
    if (!queryStateLoaded) return;
    if (querySaveTimerRef.current) clearTimeout(querySaveTimerRef.current);
    querySaveTimerRef.current = setTimeout(() => {
      saveAppsMediaQueryState({ filters, pvFilters, groupingPvs, groupingEformFields, showPvFilters })
        .catch(err => console.error('[AppsMediaReports] 儲存查詢條件失敗:', err));
    }, QUERY_SAVE_DEBOUNCE_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pvFilters, groupingPvs, groupingEformFields, showPvFilters, queryStateLoaded]);

  // 畫廊設定改變時 debounced 存回 DB
  useEffect(() => {
    if (!queryStateLoaded) return;
    if (settingsSaveTimerRef.current) clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      saveAppsMediaGallerySettings(gallerySettings)
        .catch(err => console.error('[AppsMediaReports] 儲存畫廊設定失敗:', err));
    }, SETTINGS_SAVE_DEBOUNCE_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gallerySettings, queryStateLoaded]);

  // 載入媒體數據
  const loadMediaData = useCallback(async () => {
    if (!filters.workflowName && filters.status === 'all' && !filters.startDateRange) {
      message.warning(t('reports.pleaseSetFilters') || '請先設置過濾條件');
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: 0, message: t('reports.loading') || '載入中...' });

    try {
      const grouped = await groupMediaByExecutionAndPV(
        filters,
        pvFilters,
        groupingPvs,
        (current, total) => {
          setProgress({
            current,
            total,
            message: `${t('reports.processingGroup') || '正在處理分組'}: ${current}/${total}`
          });
        },
        groupingEformFields
      );

      setGroupedMedia(grouped);
      // 新查詢後重新載入該 workflow 的已儲存勾選 + 不列印標記（若無則為空）
      if (filters.workflowName) {
        Promise.all([
          loadAppsMediaSelection(filters.workflowName),
          loadAppsMediaUserExcluded(filters.workflowName),
        ]).then(([saved, excluded]) => {
          setSelectedByGroup(saved);
          setExcludedByGroup(excluded);
        }).catch(() => { setSelectedByGroup({}); setExcludedByGroup({}); });
      } else {
        setSelectedByGroup({});
        setExcludedByGroup({});
      }

      if (grouped.length === 0) {
        message.info(t('reports.noMediaFound') || '沒有找到符合條件的媒體文件');
      } else {
        // 計算總圖片數：遍歷所有 PV 組合的所有狀態子組
        const totalImages = grouped.reduce((sum, group) => {
          const groupImages = (group.subGroups || []).reduce((subSum, subGroup) => {
            return subSum + (subGroup.mediaFiles?.length || 0);
          }, 0);
          return sum + groupImages;
        }, 0);
        
        const totalSubGroups = grouped.reduce((sum, group) => sum + (group.subGroups?.length || 0), 0);
        message.success(
          `${t('reports.found') || '找到'} ${grouped.length} ${t('reports.groups') || '個 PV 組合'}，` +
          `${totalSubGroups} ${t('reports.statusSubGroups') || '個狀態子組'}，` +
          `共 ${totalImages} ${t('reports.images') || '張圖片'}`
        );
      }
    } catch (error) {
      console.error('載入媒體數據失敗:', error);
      message.error(t('reports.loadFailed') || `載入失敗: ${error.message}`);
      setGroupedMedia([]);
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0, message: '' });
    }
  }, [filters, pvFilters, groupingPvs, groupingEformFields, t]);

  const handleExportDocx = async () => {
    if (groupedMedia.length === 0) {
      message.warning(t('reports.noDataToExport') || '沒有數據可導出');
      return;
    }

    setExporting(true);
    setProgress({
      current: 0,
      total: groupedMedia.length,
      message: t('reports.exportingDocx') || '正在導出 Word...',
    });

    try {
      await exportGalleryToDocx(
        groupedMedia,
        gallerySettings,
        'apps_media_reports',
        (current, total, msg) => {
          setProgress({ current, total, message: msg });
        },
        groupingPvs,
        selectedByGroup,
        processVariableDefinitions,
        groupingEformFields
      );
      message.success(t('reports.exportDocxSuccess') || 'Word 導出成功');
    } catch (error) {
      console.error('Docx 導出失敗:', error);
      message.error(t('reports.exportDocxFailed') || `Word 導出失敗: ${error.message}`);
    } finally {
      setExporting(false);
      setProgress({ current: 0, total: 0, message: '' });
    }
  };

  const handleExportZip = async () => {
    if (groupedMedia.length === 0) {
      message.warning(t('reports.noDataToExport') || '沒有數據可導出');
      return;
    }

    setExporting(true);
    setProgress({
      current: 0,
      total: groupedMedia.length,
      message: t('reports.exportingZip') || '正在打包 ZIP...',
    });

    try {
      await exportGalleryToZip(
        groupedMedia,
        gallerySettings,
        'apps_media_reports',
        (current, total, msg) => {
          setProgress({ current, total, message: msg });
        },
        groupingPvs,
        selectedByGroup,
        processVariableDefinitions,
        groupingEformFields
      );
      message.success(t('reports.exportZipSuccess') || 'ZIP 下載成功');
    } catch (error) {
      console.error('ZIP 下載失敗:', error);
      message.error(t('reports.exportZipFailed') || `ZIP 下載失敗: ${error.message}`);
    } finally {
      setExporting(false);
      setProgress({ current: 0, total: 0, message: '' });
    }
  };

  const handleExportOrientationChange = (e) => {
    const pdfOrientation = e.target.value;
    setGallerySettings((prev) => ({ ...prev, pdfOrientation }));
  };

  // 導出 PDF
  const handleExportPdf = async () => {
    if (groupedMedia.length === 0) {
      message.warning(t('reports.noDataToExport') || '沒有數據可導出');
      return;
    }

    const orientation = gallerySettings.pdfOrientation || 'landscape';

    setExporting(true);
    setProgress({ 
      current: 0, 
      total: groupedMedia.length, 
      message: t('reports.exportingPdf') || '正在導出 PDF...' 
    });

    try {
      const settings = {
        ...gallerySettings,
        pdfOrientation: orientation,
      };

      await exportGalleryToPdf(
        groupedMedia,
        settings,
        `apps_media_reports_${orientation}`,
        (current, total, message) => {
          setProgress({ current, total, message });
        },
        groupingPvs,
        selectedByGroup,
        processVariableDefinitions,
        groupingEformFields
      );

      message.success(t('reports.exportSuccess') || 'PDF 導出成功');
    } catch (error) {
      console.error('PDF 導出失敗:', error);
      message.error(t('reports.exportFailed') || `PDF 導出失敗: ${error.message}`);
    } finally {
      setExporting(false);
      setProgress({ current: 0, total: 0, message: '' });
    }
  };

  // 處理過濾條件改變
  const handleFiltersChange = (newFilters) => {
    console.log('[AppsMediaReportsPage] handleFiltersChange 被調用，新過濾條件:', newFilters);
    setFilters(newFilters);
  };

  // 處理流程變量過濾條件改變
  const handlePvFiltersChange = (newPvFilters) => {
    setPvFilters(newPvFilters);
  };

  // 處理分組變量改變
  const handleGroupingPvsChange = (newGroupingPvs) => {
    setGroupingPvs(newGroupingPvs);
  };

  // 處理 eForm 欄位分組改變
  const handleGroupingEformFieldsChange = (newEformFields) => {
    setGroupingEformFields(newEformFields);
  };

  const handleProcessVariablesLoaded = useCallback((definitions) => {
    setProcessVariableDefinitions(Array.isArray(definitions) ? definitions : []);
  }, []);

  // 處理畫廊設置改變
  const handleSettingsChange = (newSettings) => {
    setGallerySettings(newSettings);
  };

  // 處理查詢
  const handleSearch = () => {
    loadMediaData();
  };

  const handleResetAll = useCallback(() => {
    const defaults = getDefaultQueryState();
    setFilters(defaults.filters);
    setPvFilters(defaults.pvFilters);
    setGroupingPvs(defaults.groupingPvs);
    setGroupingEformFields(defaults.groupingEformFields);
    setShowPvFilters(defaults.showPvFilters);
    setProcessVariableDefinitions([]);
    setGroupedMedia([]);
    // 重置同時清掉 DB 中已存的查詢條件
    saveAppsMediaQueryState(defaults).catch(() => {});
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      {/* 頁面標題 */}
      <Card 
        style={{ marginBottom: '16px' }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileImageOutlined />
              {t('reports.appsMedia') || 'App\'s Media Reports'}
            </h2>
            <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
              {t('reports.appsMediaDescription') || '按流程實例和流程變量分組展示圖片媒體文件，支持 Windows Gallery 效果的自適應排版和 A4 PDF 導出'}
            </p>
            <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: '13px' }}>
              點擊圖片右上角圓圈可選取要列印的圖片；數字代表列印順序，各群組排序獨立
            </p>
          </div>
          <Space align="center" wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadMediaData}
              loading={loading}
              disabled={exporting}
            >
              {t('common.refresh') || '刷新'}
            </Button>
            <Radio.Group
              value={gallerySettings.pdfOrientation || 'landscape'}
              onChange={handleExportOrientationChange}
              disabled={exporting}
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  label: t('reports.landscape') || '橫向',
                  value: 'landscape',
                },
                {
                  label: t('reports.portrait') || '縱向',
                  value: 'portrait',
                },
              ]}
            />
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleExportPdf}
              loading={exporting}
              disabled={loading || groupedMedia.length === 0}
            >
              {t('reports.exportPdf') || '導出 PDF'}
            </Button>
            <Tooltip title={t('reports.exportDocxTooltip') || '匯出為 Word，可自行刪除圖片與調整版面'}>
              <Button
                type="primary"
                icon={<FileWordOutlined />}
                onClick={handleExportDocx}
                loading={exporting}
                disabled={loading || groupedMedia.length === 0}
              >
                {t('reports.exportDocx') || '導出 Docx'}
              </Button>
            </Tooltip>
            <Tooltip title={t('reports.exportZipTooltip') || '依分組目錄打包下載所有圖片'}>
              <Button
                icon={<FileZipOutlined />}
                onClick={handleExportZip}
                loading={exporting}
                disabled={loading || groupedMedia.length === 0}
              >
                {t('reports.exportZip') || '下載 ZIP'}
              </Button>
            </Tooltip>
            <Tooltip title={t('reports.printOutSettings') || 'Print Out Settings'}>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setSettingsModalOpen(true)}
              />
            </Tooltip>
          </Space>
        </div>
      </Card>

      {/* 進度條 */}
      {(loading || exporting) && progress.total > 0 && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Progress
            percent={progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}
            status={exporting ? 'active' : 'active'}
            format={() => `${progress.current}/${progress.total}`}
          />
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
            {progress.message}
          </div>
        </Card>
      )}

      {/* 過濾器區域 */}
      <Card 
        title={t('reports.instanceFilters') || '流程實例過濾'} 
        size="small" 
        style={{ marginBottom: '16px' }}
      >
        <InstanceFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
          onReset={handleResetAll}
          loading={loading}
          showSearchButton={!!filters.workflowName}
        />
        {!filters.workflowName && (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            backgroundColor: '#fff7e6', 
            border: '1px solid #ffd591',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            <Text type="warning" style={{ fontSize: '14px' }}>
              {t('reports.selectWorkflowNameFirst') || '請先選擇工作流名稱以開始查詢'}
            </Text>
          </div>
        )}
      </Card>

      {/* 流程變量過濾器控制按鈕和卡片 */}
      {filters.workflowName && (
        <Card 
          size="small" 
          style={{ marginBottom: '16px' }}
          extra={
            <Button
              type="text"
              size="small"
              icon={showPvFilters ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setShowPvFilters(!showPvFilters)}
            >
              {showPvFilters 
                ? (t('reports.collapse') || '收合') 
                : (t('reports.expand') || '展開')}
            </Button>
          }
          title={
            <span>
              <FilterOutlined style={{ marginRight: '8px' }} />
              {t('reports.processVariableFilters') || '流程資料過濾（用於分組）'}
            </span>
          }
        >
          <div style={{ display: showPvFilters ? 'block' : 'none' }}>
            <ProcessVariableFilters
              selectedWorkflowName={filters.workflowName}
              selectedPvFilters={pvFilters}
              onPvFiltersChange={handlePvFiltersChange}
              selectedGroupingPvs={groupingPvs}
              onGroupingPvsChange={handleGroupingPvsChange}
              onProcessVariablesLoaded={handleProcessVariablesLoaded}
              selectedGroupingEformFields={groupingEformFields}
              onGroupingEformFieldsChange={handleGroupingEformFieldsChange}
            />
          </div>
        </Card>
      )}

      {/* 畫廊設置 Modal */}
      <GallerySettings
        settings={gallerySettings}
        onSettingsChange={handleSettingsChange}
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />

      <Divider />

      {/* 媒體畫廊 */}
        <Spin spinning={loading || selectionLoading} tip={t('reports.loading') || '載入中...'}>
          {groupedMedia.length > 0 ? (
            <MediaGallery
              groupedMedia={groupedMedia}
              settings={gallerySettings}
              groupingPvs={groupingPvs}
              pvDefinitions={processVariableDefinitions}
              groupingEformFields={groupingEformFields}
              selectedByGroup={selectedByGroup}
              onSelectionChange={handleSelectionChange}
              excludedByGroup={excludedByGroup}
              onExcludedChange={handleExcludedChange}
              hideSelected={gallerySettings.hideSelected ?? true}
              hideExcluded={gallerySettings.hideExcluded ?? true}
              onHideSelectedChange={v => setGallerySettings(prev => ({ ...prev, hideSelected: v }))}
              onHideExcludedChange={v => setGallerySettings(prev => ({ ...prev, hideExcluded: v }))}
            />
        ) : !loading && (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <FileImageOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <p style={{ color: '#999', fontSize: '14px' }}>
                {t('reports.noMediaData') || '沒有媒體數據，請設置過濾條件並點擊「查詢」'}
              </p>
            </div>
          </Card>
        )}
      </Spin>
    </div>
  );
};

export default AppsMediaReportsPage;
